import type { BBox, BuildProgress, RegionRequest, StopPreview } from '../../shared/types.js';
import { bboxAreaKm2 } from '../../shared/projection.js';
import { clear, el, formatDistance, icon } from './dom.js';
import type { IconName } from './icons.js';
import type { FixtureInfo } from './lobbyScreen.js';
import {
  areaChecks,
  distanceMetres,
  firstBlocker,
  MAX_GATES,
  pinChecks,
  routeChecks,
  type Check,
  type Limits,
} from './raceChecks.js';
import {
  bearingLabel,
  RegionPicker,
  type PickerTool,
  type PreviewResult,
  type RegionEstimate,
} from './regionPicker.js';
import { BuildClock, formatElapsed, formatWait, remainingPhrase } from './buildClock.js';
import { num, serverText, t } from '../i18n/i18n.js';
import { nudge, paintBanner, paintChecklist, paintStepper } from './steps.js';
import { buildStopPreview } from './tourPreview.js';

/**
 * Drawing a race, one decision at a time.
 *
 * This replaced a form: a search box, a toolbar, a map, a row of route controls and a status
 * line, all on screen at once in a 440-pixel strip under the lobby's other panels, where the
 * same click meant "start", "finish" or "checkpoint" depending on what was already there. It
 * worked for people who already knew how it worked. Now the map has the whole screen and a
 * panel beside it asks one thing per step — where, the box, the start and finish, the shape of
 * the race, and a check — with the instructions for that step and nothing else.
 *
 * Next is never disabled. Each step keeps a checklist of what it needs, ticking itself off as
 * things are done, and pressing Next before the list is complete turns the unmet items red,
 * says in a banner what to do, and pulses the thing on the map they are about. A disabled
 * button would have said "no" and refused to say why. See DECISIONS.md §63.
 *
 * One instance serves both callers — a single race from the front door or the lobby, and a
 * stop inside a tour — and keeps its map between uses, so the second race of a cup starts
 * on the streets the first one left you looking at.
 */

export type WizardMode = 'race' | 'stop' | 'free';
type StepId = 'source' | 'area' | 'pins' | 'route' | 'check';

export interface WizardOpen {
  /**
   * `race` is a lobby's region; `stop` is one race of a tour, which also gets a name; `free` is
   * a free drive's region — a box and a start, and no finish or route to shape.
   */
  mode: WizardMode;
  /** The top bar's title: "Draw your own race", "Add a race to Kraków Cup". */
  title: string;
  /** The last step's button: "Race here", "Add to tour". */
  confirmLabel: string;
  /** A race to edit rather than a blank map. */
  initial?: { region: RegionRequest; title?: string };
  /** For a stop: what to call it if nobody types anything, e.g. "Race 3". */
  defaultTitle?: string;
  /** Skip straight to a step — editing a race starts on its pins, not its source. */
  startAt?: StepId;
}

export interface WizardResult {
  region: RegionRequest;
  title: string;
  fixtureTitle?: string;
  /** The card sketch, if the dry run got far enough to produce one. */
  preview?: StopPreview;
}

export interface RaceWizardOptions {
  /** The server's dry run: builds the region once and reports what came out. */
  onPreview(
    region: RegionRequest,
    onProgress: (progress: BuildProgress) => void,
    signal: AbortSignal,
  ): Promise<PreviewResult>;
  /** How long a box would take to build, asked while it is being drawn. */
  onEstimate(bbox: BBox, signal: AbortSignal): Promise<RegionEstimate | null>;
}

type Preview =
  | { status: 'idle' }
  | { status: 'running'; key: string; progress: BuildProgress | null }
  | { status: 'ok'; key: string; result: PreviewResult }
  | { status: 'failed'; key: string; error: string }
  /** The server could not be reached at all — worth saying, not worth refusing over. */
  | { status: 'unreachable'; key: string; error: string };

const STEP_META: Record<StepId, { label: string; icon: IconName }> = {
  source: { label: t('Where'), icon: 'travel_explore' },
  area: { label: t('Area'), icon: 'crop_free' },
  pins: { label: t('Start & finish'), icon: 'sports_score' },
  route: { label: t('Route'), icon: 'route' },
  check: { label: t('Check'), icon: 'done_all' },
};

/** How long a map hint stays up — long enough to read twice, short enough not to linger. */
const NOTICE_MS = 4200;
/** A box being dragged changes every frame; ask the server about it once it has settled. */
const ESTIMATE_DEBOUNCE_MS = 400;

export class RaceWizard {
  readonly root: HTMLElement;

  private readonly picker: RegionPicker;
  private readonly titleNode = el('h2', { class: 'wizard-title', id: 'wizard-title' });
  private readonly stepper = el('ol', { class: 'stepper', id: 'wizard-stepper' });
  private readonly panelBody = el('div', { class: 'wizard-panel-body' });
  private readonly banner = el('div', { class: 'banner-alert is-hidden', id: 'wizard-banner', role: 'alert' });
  private readonly checklist = el('ul', { class: 'checklist', id: 'wizard-checklist' });
  private readonly backButton: HTMLButtonElement;
  private readonly nextButton: HTMLButtonElement;
  private readonly stepCount = el('span', { class: 'wizard-step-count' });
  private readonly notice = el('div', { class: 'map-notice is-hidden', role: 'status' });
  private noticeTimer = 0;

  private fixtures: FixtureInfo[] = [];
  private limits: Limits = { maxAreaKm2: 4, minAreaKm2: 0.05, offline: false };

  private request: WizardOpen | null = null;
  private resolve: ((result: WizardResult | null) => void) | null = null;
  private step: StepId = 'area';
  private source: { kind: 'fixture'; id: string } | { kind: 'draw' } | null = null;
  /** Set by a press of Next that was refused, and cleared by moving step. */
  private attempted = false;
  private preview: Preview = { status: 'idle' };
  /** Stops polling a dry run nobody is waiting for any more. */
  private previewAbort: AbortController | null = null;
  private readonly clock = new BuildClock();
  private clockTimer = 0;
  /** What the server said about the box as it stands, keyed by the box it was asked about. */
  private estimate: { key: string; value: RegionEstimate | null } | null = null;
  private estimateAbort: AbortController | null = null;
  private estimateTimer = 0;
  private name = '';
  /** The step's own controls repaint from state; this is what they hang off. */
  private refreshControls: () => void = () => {};

  constructor(private readonly options: RaceWizardOptions) {
    this.picker = new RegionPicker({
      maxAreaKm2: () => this.limits.maxAreaKm2,
      onChange: () => this.onPickerChange(),
      onNotice: (message) => this.showNotice(message),
    });

    const closeButton = el(
      'button',
      { class: 'icon-button', type: 'button', id: 'wizard-close', title: t('Close') },
      icon('close'),
    );
    closeButton.setAttribute('aria-label', t('Close'));
    closeButton.addEventListener('click', () => this.cancel());

    this.backButton = el('button', { class: 'button button-ghost', type: 'button', id: 'wizard-back' }, icon('arrow_back'), t('Back'));
    this.backButton.addEventListener('click', () => this.back());
    this.nextButton = el('button', { class: 'button button-primary', type: 'button', id: 'wizard-next' }, t('Next'));
    this.nextButton.addEventListener('click', () => void this.next());

    this.root = el(
      'div',
      { class: 'fullscreen-dialog wizard is-hidden', id: 'race-wizard', role: 'dialog' },
      el(
        'header',
        { class: 'top-app-bar' },
        closeButton,
        this.titleNode,
        this.stepper,
      ),
      el(
        'div',
        { class: 'wizard-body' },
        el(
          'section',
          { class: 'wizard-panel' },
          this.panelBody,
          el(
            'div',
            { class: 'wizard-requirements' },
            el('h3', { class: 'wizard-requirements-title' }, t('To continue')),
            this.checklist,
          ),
          this.banner,
          el('div', { class: 'wizard-actions' }, this.backButton, this.stepCount, this.nextButton),
        ),
        el('div', { class: 'wizard-map' }, this.picker.root, this.notice),
      ),
    );
    this.root.setAttribute('aria-labelledby', 'wizard-title');
  }

  setFixtures(fixtures: FixtureInfo[]): void {
    this.fixtures = fixtures;
  }

  setLimits(limits: Partial<Limits>): void {
    this.limits = { ...this.limits, ...limits };
  }

  get isOpen(): boolean {
    return !this.root.classList.contains('is-hidden');
  }

  /**
   * Opens on a blank map or on a race to edit, and resolves with the race once the last
   * step is confirmed — or with null if it is closed instead.
   */
  open(request: WizardOpen): Promise<WizardResult | null> {
    // Opening over an unfinished one abandons it; there is only one map.
    this.resolve?.(null);
    this.request = request;
    this.titleNode.textContent = request.title;
    // Before the region is loaded, so a race being turned into a free drive loses its finish.
    this.picker.setStartOnly(request.mode === 'free');
    this.preview = { status: 'idle' };
    // A stop's suggested name is a real value rather than a placeholder, so the field that
    // shows "Race 3" is not then refused for being empty.
    this.name = request.initial?.title ?? (request.mode === 'stop' ? (request.defaultTitle ?? '') : '');

    const region = request.initial?.region ?? null;
    if (region?.fixture) {
      const fixture = this.fixtures.find((f) => f.id === region.fixture);
      this.source = { kind: 'fixture', id: region.fixture };
      this.picker.load(region, fixture);
      if (request.mode === 'stop' && !request.initial?.title) this.name = fixture?.title ?? this.name;
    } else if (region) {
      this.source = { kind: 'draw' };
      this.picker.load(region);
    } else {
      // Drawn unless somebody picks otherwise: the built-in regions are four, and the point of
      // a tour is usually the city it is in.
      this.source = { kind: 'draw' };
      this.picker.reset();
    }

    this.root.classList.remove('is-hidden');
    this.goTo(request.startAt && this.steps().includes(request.startAt) ? request.startAt : this.steps()[0]);
    // Leaflet measures its container on init, and the dialog was display:none until now.
    requestAnimationFrame(() => {
      this.picker.invalidateSize();
      if (region) this.picker.fitBox();
    });
    return new Promise((resolve) => {
      this.resolve = resolve;
    });
  }

  /** Escape, the close button, or the caller giving up. */
  cancel(): void {
    if (!this.isOpen) return;
    const draft = this.picker.draft;
    // Only worth a question once there is something to lose.
    const drawn = draft.bbox !== null && this.source?.kind === 'draw' && !this.request?.initial;
    if (drawn && !window.confirm(t('Discard this race? The box and pins you placed will be lost.'))) return;
    this.finish(null);
  }

  private finish(result: WizardResult | null): void {
    this.root.classList.add('is-hidden');
    this.hideNotice();
    this.stopPreview();
    this.estimateAbort?.abort();
    window.clearTimeout(this.estimateTimer);
    const resolve = this.resolve;
    this.resolve = null;
    this.request = null;
    resolve?.(result);
  }

  // -------------------------------------------------------------------------
  // Steps
  // -------------------------------------------------------------------------

  /**
   * The steps this race goes through, which depend on what it is.
   *
   * A built-in region has its box already — it is the whole reason it works offline — so it
   * skips straight from choosing it to its pins. A lobby race is always drawn: the built-in
   * regions are cards in the lobby already, and offering them again here would be the same
   * choice twice.
   */
  private steps(): StepId[] {
    const fixture = this.source?.kind === 'fixture';
    const steps: StepId[] = [];
    if (this.request?.mode === 'stop') steps.push('source');
    if (!fixture) steps.push('area');
    // A free drive has no route to shape: nobody follows it.
    steps.push(...(this.request?.mode === 'free' ? (['pins', 'check'] as const) : (['pins', 'route', 'check'] as const)));
    return steps;
  }

  /** A free drive's pins step places a start and nothing else, and says so. */
  private stepLabel(step: StepId): string {
    return step === 'pins' && this.request?.mode === 'free' ? t('Start') : STEP_META[step].label;
  }

  private goTo(step: StepId): void {
    this.step = step;
    this.attempted = false;
    this.picker.setTool(TOOLS[step]);
    this.picker.setSearchVisible(step === 'area' || step === 'source');
    if (step !== 'check') this.picker.clearPreview();
    this.renderStep();
    this.paint();
    if (step === 'check') void this.runPreview();
    if (step === 'area') this.scheduleEstimate();
    // The panel's heading is where a screen reader should land, not the button just pressed.
    this.panelBody.querySelector<HTMLElement>('.wizard-step-title')?.focus({ preventScroll: true });
  }

  private back(): void {
    const steps = this.steps();
    const index = steps.indexOf(this.step);
    if (index <= 0) {
      this.cancel();
      return;
    }
    this.goTo(steps[index - 1]);
  }

  /**
   * On to the next step, or the reason why not.
   *
   * The refusal is the feature: the unmet items go red with their explanations, the banner
   * leads with the first of them, and the map pulses whatever it is about.
   */
  private async next(): Promise<void> {
    const blocker = firstBlocker(this.checks());
    if (blocker) {
      this.refuse(blocker);
      return;
    }
    const steps = this.steps();
    const index = steps.indexOf(this.step);
    if (index < steps.length - 1) {
      this.goTo(steps[index + 1]);
      return;
    }
    this.confirm();
  }

  private refuse(blocker: Check): void {
    this.attempted = true;
    this.paint();
    if (blocker.target) this.picker.flash(blocker.target);
    if (blocker.target === 'name') this.panelBody.querySelector<HTMLInputElement>('#wizard-name')?.focus();
    nudge(this.banner);
  }

  /** Jumping along the stepper: backwards freely, forwards only through steps that pass. */
  private jump(target: StepId): void {
    const steps = this.steps();
    const to = steps.indexOf(target);
    const from = steps.indexOf(this.step);
    if (to <= from) {
      this.goTo(target);
      return;
    }
    for (let i = from; i < to; i++) {
      const blocker = firstBlocker(this.checksFor(steps[i]));
      if (blocker) {
        if (steps[i] !== this.step) this.goTo(steps[i]);
        this.refuse(blocker);
        return;
      }
    }
    this.goTo(target);
  }

  private confirm(): void {
    const region = this.picker.region();
    if (!region || !this.request) return;
    const fixture = region.fixture ? this.fixtures.find((f) => f.id === region.fixture) : undefined;
    const result =
      this.preview.status === 'ok'
        ? buildStopPreview(this.preview.result.preview ?? [], this.preview.result.route ?? [])
        : null;
    this.finish({
      region,
      title: this.name.trim() || this.request.defaultTitle || fixture?.title || t('Your own region'),
      ...(fixture ? { fixtureTitle: fixture.title } : {}),
      ...(result ? { preview: result } : {}),
    });
  }

  // -------------------------------------------------------------------------
  // Checks
  // -------------------------------------------------------------------------

  private checks(): Check[] {
    return this.checksFor(this.step);
  }

  private checksFor(step: StepId): Check[] {
    const draft = this.picker.draft;
    switch (step) {
      case 'source':
        return [
          {
            id: 'source',
            label: t('Choose a built-in region or draw your own'),
            ok: this.source !== null,
            level: 'block',
            problem: t('Nothing is chosen yet. Pick one of the regions above, or “Draw on the map”.'),
            target: 'list',
          },
        ];
      case 'area':
        return areaChecks(draft, this.limits);
      case 'pins':
        // Only the start matters to a free drive; every check about the finish is moot.
        return this.request?.mode === 'free'
          ? pinChecks(draft).filter((check) => check.id === 'start' || check.id === 'start-inside')
          : pinChecks(draft);
      case 'route':
        return routeChecks(draft);
      case 'check':
        return this.reviewChecks();
    }
  }

  private reviewChecks(): Check[] {
    const checks: Check[] = [];
    if (this.request?.mode === 'stop') {
      checks.push({
        id: 'name',
        label: t('Give the race a name'),
        ok: this.name.trim().length > 0,
        level: 'block',
        problem: t('The race needs a name — it is what the tour card and the results call it.'),
        target: 'name',
      });
    }
    const p = this.preview;
    checks.push({
      id: 'built',
      label: t('The region builds into a race'),
      ok: p.status === 'ok' || p.status === 'unreachable',
      level: 'block',
      problem:
        p.status === 'failed'
          ? t(
              'The server could not make a race here — its reason is above. Go back and move the box or the pins, or press “Try again” if the problem was the network.',
            )
          : t(
              'Still building the region to check it can be raced. A built-in region takes a second; somewhere new can take a minute while its streets download.',
            ),
    });
    if (p.status === 'unreachable') {
      checks.push({
        id: 'unchecked',
        label: t('Checked with the server'),
        ok: false,
        level: 'warn',
        problem: t(
          'The server could not be reached to check this region. You can still use it, but it may fail to load when the race starts.',
        ),
      });
    }
    return checks;
  }

  // -------------------------------------------------------------------------
  // Painting
  // -------------------------------------------------------------------------

  private onPickerChange(): void {
    // A different race means the last check no longer describes it.
    if (this.step !== 'check' && this.preview.status !== 'idle') {
      this.stopPreview();
      this.preview = { status: 'idle' };
    }
    if (this.step === 'area') this.scheduleEstimate();
    this.refreshControls();
    this.paint();
  }

  /** The stepper, the checklist, the banner and the buttons — everything that follows state. */
  private paint(): void {
    if (!this.request) return;
    const steps = this.steps();
    const index = steps.indexOf(this.step);

    paintStepper(
      this.stepper,
      steps.map((id) => ({ id, label: this.stepLabel(id) })),
      index,
      (id) => this.jump(id as StepId),
    );
    const checks = this.checks();
    paintChecklist(this.checklist, checks, this.attempted);
    paintBanner(this.banner, checks, this.attempted);

    this.stepCount.textContent = t('Step {n} of {total}', { n: index + 1, total: steps.length });
    this.backButton.lastChild!.textContent = index === 0 ? t('Cancel') : t('Back');
    const last = index === steps.length - 1;
    clear(this.nextButton);
    this.nextButton.append(last ? icon('check') : '', last ? this.request.confirmLabel : t('Next'), last ? '' : icon('arrow_forward'));
    // Not disabled — still pressable, and pressing it says why — but it should not look
    // ready either, so the person can see at a glance that something is left to do.
    this.nextButton.classList.toggle('is-pending', firstBlocker(checks) !== null);
  }

  private renderStep(): void {
    clear(this.panelBody);
    window.clearInterval(this.clockTimer);
    this.clockTimer = 0;
    this.refreshControls = () => {};
    const head = (title: string, ...intro: (string | Node)[]) => {
      const heading = el('h3', { class: 'wizard-step-title', tabIndex: -1 }, title);
      this.panelBody.append(
        el('p', { class: 'wizard-overline' }, this.stepLabel(this.step)),
        heading,
        ...intro.map((line) => (typeof line === 'string' ? el('p', { class: 'wizard-intro' }, line) : line)),
      );
    };

    switch (this.step) {
      case 'source':
        head(
          t('Where is this race?'),
          t('Start from one of the built-in regions — they load in seconds and work with no network — or draw a box anywhere in the world.'),
        );
        this.renderSource();
        break;
      case 'area':
        head(t('Mark out the area'));
        this.renderArea();
        break;
      case 'pins':
        if (this.request?.mode === 'free') {
          head(
            t('Place the start'),
            t('Click the map where everyone sets off from. It snaps to the nearest street when the city is built, so close is good enough. There is no finish: it is a free drive.'),
          );
        } else {
          head(
            t('Place the start and the finish'),
            t('Click the map to drop the start, then click again for the finish. Both snap to the nearest street when the city is built, so close is good enough.'),
          );
        }
        this.renderPins();
        break;
      case 'route':
        head(
          t('Shape the race'),
          t('Optional. Leave both as they are and the race takes the quickest way from start to finish, with checkpoints spaced along it.'),
        );
        this.renderRoute();
        break;
      case 'check':
        if (this.request?.mode === 'free') {
          head(
            t('Check the region'),
            t('The server builds the region once to make sure it can be driven. It is saved in this browser under the name below, so next time it is one click in the lobby.'),
          );
        } else {
          head(
            t('Check the race'),
            t('The server builds the region once to make sure it can be raced. The highlighted line on the map is the route the cars will drive.'),
          );
        }
        this.renderCheck();
        break;
    }
  }

  private renderSource(): void {
    const list = el('div', { class: 'choice-list', id: 'wizard-sources', role: 'radiogroup' });
    const option = (key: string, title: string, sub: string, glyph: IconName, pick: () => void) => {
      const button = el(
        'button',
        { class: 'choice-option', type: 'button', role: 'radio', dataset: { source: key } },
        icon(glyph, 'choice-option-icon'),
        el('span', { class: 'choice-option-text' }, el('span', { class: 'choice-option-title' }, title), el('span', { class: 'choice-option-sub' }, sub)),
        icon('check_circle', 'choice-option-check'),
      );
      button.addEventListener('click', pick);
      list.append(button);
      return button;
    };
    // First, and chosen already: most races are somewhere none of the built-in regions is.
    option('draw', t('Draw on the map'), t('Any city, any streets — up to {max} km²', { max: num(this.limits.maxAreaKm2) }), 'draw', () => {
      if (this.source?.kind !== 'draw') {
        if (this.nameIsSuggested()) this.name = this.request?.defaultTitle ?? '';
        this.picker.reset();
      }
      this.source = { kind: 'draw' };
      this.picker.focusSearch();
      this.refreshControls();
      this.paint();
    });
    for (const fixture of this.fixtures) {
      option(fixture.id, serverText(fixture.title), `${num(fixture.areaKm2)} km²`, 'map', () => {
        this.source = { kind: 'fixture', id: fixture.id };
        // Follows the choice for as long as nobody has typed a name of their own.
        if (this.nameIsSuggested()) this.name = fixture.title;
        this.picker.load({ fixture: fixture.id, bbox: fixture.bbox, seed: 1 }, fixture);
      });
    }
    this.panelBody.append(list);
    this.refreshControls = () => {
      for (const node of list.querySelectorAll<HTMLElement>('.choice-option')) {
        const key = node.dataset.source;
        const on =
          (this.source?.kind === 'draw' && key === 'draw') ||
          (this.source?.kind === 'fixture' && this.source.id === key);
        node.classList.toggle('is-selected', on);
        node.setAttribute('aria-checked', String(on));
      }
    };
    this.refreshControls();
  }

  private renderArea(): void {
    const steps = el(
      'ol',
      { class: 'instructions' },
      el(
        'li',
        {},
        el('strong', {}, t('Find the place. ')),
        t('Search above the map, or pick “Move the map” and drag. The right mouse button, or Space held down, moves it at any time.'),
      ),
      el(
        'li',
        {},
        el('strong', {}, t('Draw a box. ')),
        t('Pick “Draw the box”, then press and drag across the map. Drag a corner to resize it, or the middle to move it.'),
      ),
    );
    // What a left-button drag does. It used to be "draw" only, so the map could not be moved
    // except by zooming out and back in.
    const gestures = el('div', { class: 'segmented', role: 'group', ariaLabel: t('What dragging the map does') });
    const gesture = (which: 'pan' | 'draw', label: string, glyph: IconName) => {
      const button = el(
        'button',
        { class: 'segment', type: 'button', id: `box-${which}` },
        icon(glyph, 'segment-icon'),
        el('span', { class: 'segment-label' }, label),
      );
      button.addEventListener('click', () => this.picker.setBoxGesture(which));
      gestures.append(button);
      return button;
    };
    const panButton = gesture('pan', t('Move the map'), 'open_with');
    const drawButton = gesture('draw', t('Draw the box'), 'crop_free');
    const useView = el(
      'button',
      { class: 'button button-tonal', type: 'button', id: 'picker-use-view' },
      icon('crop_free'),
      t('Use current view'),
    );
    useView.addEventListener('click', () => this.picker.useCurrentView());
    const clearBox = el('button', { class: 'button button-ghost', type: 'button', id: 'picker-clear' }, icon('restart_alt'), t('Clear box'));
    clearBox.addEventListener('click', () => {
      this.picker.clearBox();
      this.picker.setBoxGesture('draw');
    });

    const meterFill = el('span', { class: 'meter-fill' });
    const meterText = el('span', { class: 'meter-value' });
    const meterLimit = el('span', { class: 'meter-limit' });
    const meter = el(
      'div',
      { class: 'area-meter', id: 'area-meter' },
      el('div', { class: 'meter-head' }, el('span', { class: 'meter-label' }, t('Area')), meterText, meterLimit),
      el('div', { class: 'meter-track' }, meterFill),
    );
    const estimate = el('div', { class: 'build-estimate', id: 'area-estimate', role: 'status' });
    this.panelBody.append(steps, gestures, el('div', { class: 'button-row' }, useView, clearBox), meter, estimate);

    this.refreshControls = () => {
      const mode = this.picker.boxGestureMode;
      for (const [button, which] of [
        [panButton, 'pan'],
        [drawButton, 'draw'],
      ] as const) {
        button.classList.toggle('is-selected', mode === which);
        button.setAttribute('aria-pressed', String(mode === which));
      }
      const bbox = this.picker.draft.bbox;
      const area = bbox ? bboxAreaKm2(bbox) : 0;
      const max = this.limits.maxAreaKm2;
      meterText.textContent = bbox ? `${num(area, 2)} km²` : t('no box yet');
      meterLimit.textContent = t('of {max} km²', { max: num(max) });
      meterFill.style.width = `${Math.min(100, (area / max) * 100)}%`;
      meter.classList.toggle('is-over', area > max);
      meter.classList.toggle('is-under', bbox !== null && area < this.limits.minAreaKm2);
      clearBox.hidden = !bbox;
      this.paintEstimate(estimate, bbox && area <= max && area >= this.limits.minAreaKm2 ? bbox : null);
    };
    this.refreshControls();
  }

  private renderPins(): void {
    const segmented = el('div', { class: 'segmented', role: 'group', ariaLabel: t('Which pin the next click places') });
    const make = (which: 'start' | 'finish', label: string, glyph: IconName) => {
      const button = el(
        'button',
        { class: 'segment', type: 'button', id: `pin-${which}`, dataset: { pin: which } },
        icon(glyph, 'segment-icon'),
        el('span', { class: 'segment-label' }, label),
        el('span', { class: 'segment-state' }),
      );
      button.addEventListener('click', () => this.picker.armPin(this.picker.armedPin === which ? null : which));
      segmented.append(button);
      return button;
    };
    const startButton = make('start', t('Start'), 'play_arrow');
    const finishButton = make('finish', t('Finish'), 'sports_score');
    const free = this.request?.mode === 'free';
    finishButton.hidden = free;
    const hint = el('p', { class: 'wizard-hint' });
    const distance = el('p', { class: 'wizard-fact' });
    const clearPins = el('button', { class: 'button button-ghost', type: 'button', id: 'pins-clear' }, icon('restart_alt'), t('Clear both pins'));
    clearPins.addEventListener('click', () => this.picker.clearPins());
    this.panelBody.append(segmented, hint, distance, el('div', { class: 'button-row' }, clearPins));

    this.refreshControls = () => {
      const draft = this.picker.draft;
      const armed = this.picker.armedPin;
      for (const [button, which] of [
        [startButton, 'start'],
        [finishButton, 'finish'],
      ] as const) {
        const placed = which === 'start' ? draft.start : draft.finish;
        button.classList.toggle('is-selected', armed === which);
        button.setAttribute('aria-pressed', String(armed === which));
        button.querySelector('.segment-state')!.textContent = placed ? t('placed') : t('not placed');
        button.classList.toggle('is-placed', Boolean(placed));
      }
      const moving = armed === 'start' ? draft.start : draft.finish;
      hint.textContent =
        armed === 'start'
          ? moving
            ? t('Click the map to move the start.')
            : t('Click the map to place the start.')
          : armed === 'finish'
            ? moving
              ? t('Click the map to move the finish.')
              : t('Click the map to place the finish.')
            : t('Drag a pin to move it, or pick Start or Finish above and click the map.');
      distance.textContent =
        draft.start && draft.finish
          ? t('{distance} apart as the crow flies — the race itself follows the streets.', {
              distance: formatDistance(distanceMetres(draft.start, draft.finish)),
            })
          : '';
      clearPins.hidden = !draft.start && !draft.finish;
    };
    this.refreshControls();
  }

  private renderRoute(): void {
    // --- direction -----------------------------------------------------------
    const directionSwitch = el('input', { type: 'checkbox', class: 'switch', id: 'picker-heading', role: 'switch' });
    directionSwitch.addEventListener('change', () => this.picker.setHeading(directionSwitch.checked));
    const directionValue = el('span', { class: 'setting-value', id: 'picker-heading-value' });
    const direction = el(
      'div',
      { class: 'setting' },
      el(
        'label',
        { class: 'setting-row', htmlFor: 'picker-heading' },
        icon('navigation', 'setting-icon'),
        el(
          'span',
          { class: 'setting-text' },
          el('span', { class: 'setting-title' }, t('Choose the start direction')),
          el(
            'span',
            { class: 'setting-sub' },
            t('An arrow appears on the start pin. Drag it round and the race sets off that way.'),
          ),
        ),
        directionSwitch,
      ),
      directionValue,
    );

    // --- checkpoints ---------------------------------------------------------
    const modes = el('div', { class: 'segmented', role: 'group', ariaLabel: t('Checkpoints') });
    const modeButton = (key: 'auto' | 'count' | 'place', label: string) => {
      const button = el('button', { class: 'segment', type: 'button', dataset: { gateMode: key }, id: `gates-${key}` }, el('span', { class: 'segment-label' }, label));
      modes.append(button);
      return button;
    };
    const auto = modeButton('auto', t('Automatic'));
    const count = modeButton('count', t('How many'));
    const place = modeButton('place', t('On the map'));
    const countSelect = el('select', { class: 'select', id: 'picker-gate-count' });
    for (let n = 1; n <= MAX_GATES; n++) countSelect.append(el('option', { value: String(n) }, String(n)));
    // Three, until somebody says otherwise: one gate is barely a route and twelve is a slalom.
    countSelect.value = '3';
    countSelect.addEventListener('change', () => this.picker.setGateCount(Number(countSelect.value)));
    const countRow = el(
      'label',
      { class: 'field-label inline-field', htmlFor: 'picker-gate-count' },
      t('Checkpoints along the route'),
      countSelect,
    );
    const placeHint = el('p', { class: 'wizard-hint' });
    const clearGates = el('button', { class: 'button button-ghost', type: 'button', id: 'picker-gate-clear' }, icon('restart_alt'), t('Remove all'));
    clearGates.addEventListener('click', () => this.picker.clearGates());
    const placeRow = el('div', { class: 'gate-place' }, placeHint, clearGates);

    /**
     * Placed gates and a count are two different instructions — "go through these" and "use
     * about this many" — and answering both at once is not a thing to design for, so the mode
     * is one choice and switching it clears the other.
     */
    let mode: 'auto' | 'count' | 'place' = 'auto';
    const draft0 = this.picker.draft;
    mode = draft0.gates.length ? 'place' : draft0.gateCount !== null ? 'count' : 'auto';
    const setMode = (next: typeof mode) => {
      mode = next;
      if (mode !== 'place' && this.picker.draft.gates.length) this.picker.clearGates();
      if (mode === 'count') this.picker.setGateCount(Number(countSelect.value));
      else this.picker.setGateCount(null);
      this.picker.setTool(mode === 'place' ? 'gates' : 'route');
      this.refreshControls();
    };
    auto.addEventListener('click', () => setMode('auto'));
    count.addEventListener('click', () => setMode('count'));
    place.addEventListener('click', () => setMode('place'));
    this.picker.setTool(mode === 'place' ? 'gates' : 'route');

    const gates = el(
      'div',
      { class: 'setting' },
      el(
        'div',
        { class: 'setting-row' },
        icon('flag', 'setting-icon'),
        el(
          'span',
          { class: 'setting-text' },
          el('span', { class: 'setting-title' }, t('Checkpoints')),
          el(
            'span',
            { class: 'setting-sub' },
            t('Gates every car has to pass through, in order, so nobody cuts across town.'),
          ),
        ),
      ),
      modes,
      countRow,
      placeRow,
    );
    this.panelBody.append(direction, gates);

    this.refreshControls = () => {
      const draft = this.picker.draft;
      directionSwitch.checked = draft.startBearing !== null;
      directionSwitch.disabled = !draft.start;
      directionValue.textContent =
        draft.startBearing !== null
          ? t('Sets off {bearing} — drag the arrow on the map to turn it.', { bearing: bearingLabel(draft.startBearing) })
          : t('Off: the race leaves whichever way the quickest route goes.');
      for (const [button, key] of [
        [auto, 'auto'],
        [count, 'count'],
        [place, 'place'],
      ] as const) {
        button.classList.toggle('is-selected', mode === key);
        button.setAttribute('aria-pressed', String(mode === key));
      }
      countRow.hidden = mode !== 'count';
      if (draft.gateCount !== null) countSelect.value = String(draft.gateCount);
      placeRow.hidden = mode !== 'place';
      placeHint.textContent = draft.gates.length
        ? t(
            '{n} of {max} placed. Click the map for another, click one to remove it, drag one to move it. The race goes through them in order.',
            { n: draft.gates.length, max: MAX_GATES },
          )
        : t('Click the map to drop checkpoint 1, then 2, and so on. The race is routed through them in order.');
      clearGates.hidden = !draft.gates.length;
    };
    this.refreshControls();
  }

  private renderCheck(): void {
    // A stop needs a name for the standings; a drawn race or free drive gets one so it can be
    // found again among the saved regions in the lobby, and falls back to a default if left.
    if (this.request) {
      const stop = this.request.mode === 'stop';
      const nameInput = el('input', {
        class: 'field',
        type: 'text',
        maxLength: 40,
        id: 'wizard-name',
        value: this.name,
        placeholder: this.request.defaultTitle ?? (stop ? t('Old Town sprint') : t('Your own region')),
        autocomplete: 'off',
      });
      nameInput.addEventListener('input', () => {
        this.name = nameInput.value;
        this.paint();
      });
      nameInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') void this.next();
      });
      this.panelBody.append(
        el(
          'label',
          { class: 'field-label', htmlFor: 'wizard-name' },
          stop ? t('Race name') : t('Name'),
          nameInput,
        ),
        el(
          'p',
          { class: 'field-support' },
          stop
            ? t('Shown on the tour card and in the results.')
            : t('Kept in this browser with your saved regions, so you do not have to draw it again.'),
        ),
      );
    }

    const status = el('div', { class: 'preview-status', id: 'wizard-preview' });
    this.panelBody.append(status);
    this.refreshControls = () => {
      clear(status);
      window.clearInterval(this.clockTimer);
      this.clockTimer = 0;
      const p = this.preview;
      status.className = `preview-status is-${p.status}`;
      if (p.status === 'running' || p.status === 'idle') {
        this.paintRunning(status, p.status === 'running' ? p.progress : null);
      } else if (p.status === 'ok') {
        const stats = el('dl', { class: 'stat-grid' });
        for (const { label, value } of RegionPicker.stats(p.result)) {
          stats.append(el('div', { class: 'stat-tile' }, el('dt', {}, label), el('dd', {}, value)));
        }
        status.append(
          el('p', { class: 'preview-status-text is-ok' }, icon('check_circle'), t('Ready to race.')),
          stats,
        );
      } else {
        const retry = el('button', { class: 'button button-tonal', type: 'button', id: 'wizard-retry' }, icon('restart_alt'), t('Try again'));
        retry.addEventListener('click', () => void this.runPreview(true));
        status.append(
          el(
            'p',
            { class: `preview-status-text ${p.status === 'failed' ? 'is-error' : 'is-warn'}` },
            icon(p.status === 'failed' ? 'error' : 'cloud_off'),
            p.status === 'failed' ? t('This region cannot be raced as it is.') : t('Could not check it.'),
          ),
          // The server's own words, because "it failed" with no reason is the thing this whole
          // wizard exists to stop saying.
          el('p', { class: 'preview-status-reason', id: 'wizard-preview-reason' }, serverText(p.error)),
          retry,
        );
      }
    };
    this.refreshControls();
  }

  /**
   * The dry run, once per version of the race.
   *
   * It is the point of the last step: a region that cannot be raced should fail here, while
   * one person is looking at it, rather than in front of a lobby — and the work is not wasted
   * either way, because the world it builds is cached for when the race is run.
   */
  private async runPreview(force = false): Promise<void> {
    const region = this.picker.region();
    if (!region) return;
    const key = JSON.stringify(region);
    if (!force && this.preview.status !== 'idle' && 'key' in this.preview && this.preview.key === key) {
      if (this.preview.status === 'ok') this.picker.showPreview(this.preview.result);
      return;
    }
    this.stopPreview();
    const abort = new AbortController();
    this.previewAbort = abort;
    this.preview = { status: 'running', key, progress: null };
    this.clock.update(null);
    this.refreshControls();
    this.paint();
    const onProgress = (progress: BuildProgress) => {
      if (this.preview.status !== 'running' || this.preview.key !== key) return;
      this.preview = { ...this.preview, progress };
      this.clock.update(progress);
      if (this.step === 'check') this.refreshControls();
    };
    try {
      const result = await this.options.onPreview(region, onProgress, abort.signal);
      if (!('key' in this.preview) || this.preview.key !== key) return;
      this.preview = result.ok
        ? { status: 'ok', key, result }
        : { status: 'failed', key, error: result.error ?? t('the server did not say why.') };
      // Kept either way — coming back to the check reuses it — but only drawn, and only
      // turned into news, if somebody is still on the step that asked for it.
      if (this.step === 'check' && this.isOpen) {
        // A failed build is news whether or not anybody has pressed the button yet.
        if (!result.ok) this.attempted = true;
        else {
          this.picker.showPreview(result);
          this.picker.fitBox();
        }
      }
    } catch (err) {
      if (abort.signal.aborted || !('key' in this.preview) || this.preview.key !== key) return;
      this.preview = { status: 'unreachable', key, error: (err as Error).message };
    }
    if (this.previewAbort === abort) this.previewAbort = null;
    if (this.step === 'check') {
      this.refreshControls();
      this.paint();
    }
  }

  private stopPreview(): void {
    this.previewAbort?.abort();
    this.previewAbort = null;
  }

  /**
   * The dry run while it runs: what the server is doing, a real bar once it has said, and the
   * two clocks. Only the clock line is touched twice a second — rebuilding the bar each tick
   * would restart its transition.
   */
  private paintRunning(status: HTMLElement, progress: BuildProgress | null): void {
    const fill = el('span', { class: 'progress-bar' });
    if (progress) fill.style.width = `${Math.round(Math.max(0, Math.min(1, progress.fraction)) * 100)}%`;
    const clockLine = el('p', { class: 'build-clock tabular', id: 'wizard-build-clock' });
    const paintClock = () => {
      const elapsed = formatElapsed(this.clock.elapsedSeconds());
      const left = remainingPhrase(this.clock.remainingSeconds());
      clockLine.textContent = left ? `${elapsed} · ${left}` : elapsed;
    };
    paintClock();
    status.append(
      el(
        'p',
        { class: 'preview-status-text' },
        progress ? serverText(progress.label) : t('Building the region to check it…'),
      ),
      el('div', { class: `progress-linear${progress ? '' : ' is-indeterminate'}` }, fill),
      ...(progress?.detail ? [el('p', { class: 'build-detail' }, serverText(progress.detail))] : []),
      clockLine,
      ...(progress?.downloading
        ? [
            el(
              'p',
              { class: 'build-note' },
              t('First time here, so the streets are downloading. Next time is quick.'),
            ),
          ]
        : []),
    );
    if (!this.clockTimer) this.clockTimer = window.setInterval(() => this.tickClock(), 500);
    this.tickClock = paintClock;
  }

  /** Repaints the running clock; swapped for the current one by `paintRunning`. */
  private tickClock: () => void = () => {};

  /** Asks the server about the box once it stops moving. */
  private scheduleEstimate(): void {
    window.clearTimeout(this.estimateTimer);
    const bbox = this.picker.draft.bbox;
    if (!bbox || this.source?.kind !== 'draw') return;
    const key = JSON.stringify(bbox);
    if (this.estimate?.key === key) return;
    this.estimateTimer = window.setTimeout(() => void this.fetchEstimate(bbox, key), ESTIMATE_DEBOUNCE_MS);
  }

  private async fetchEstimate(bbox: BBox, key: string): Promise<void> {
    this.estimateAbort?.abort();
    const abort = new AbortController();
    this.estimateAbort = abort;
    let value: RegionEstimate | null = null;
    try {
      value = await this.options.onEstimate(bbox, abort.signal);
    } catch {
      // No estimate is not a problem worth showing; the check step still builds it.
    }
    if (abort.signal.aborted) return;
    this.estimate = { key, value };
    if (this.step === 'area') this.refreshControls();
  }

  /**
   * How long the box will take to build, said before anybody commits to it.
   *
   * On a server that fetches from public Overpass the answer ranges from "seconds" to "five
   * minutes" depending only on whether somebody has raced nearby before, and nothing else on
   * this screen can tell those two apart. Shown for a box the area checks accept; a box that
   * is too big has a better thing to say about it already.
   */
  private paintEstimate(node: HTMLElement, bbox: BBox | null): void {
    clear(node);
    node.className = 'build-estimate';
    if (!bbox) {
      node.hidden = true;
      return;
    }
    node.hidden = false;
    const known = this.estimate?.key === JSON.stringify(bbox) ? this.estimate : null;
    const line = (glyph: IconName, text: string, extra?: string) => {
      node.append(icon(glyph, 'build-estimate-icon'), el('span', {}, text, ...(extra ? [el('span', { class: 'build-estimate-detail' }, extra)] : [])));
    };
    if (!known) {
      node.classList.add('is-pending');
      line('schedule', t('Working out how long this takes to build…'));
      return;
    }
    const e = known.value;
    if (!e || !e.tiles) {
      node.hidden = true;
      return;
    }
    const missing = e.tiles - e.cached;
    if (!missing) {
      node.classList.add('is-ready');
      line('check_circle', t('The server already has these streets, so this builds in seconds.'));
    } else if (e.offline) {
      node.classList.add('is-warn');
      line('cloud_off', t('The server is offline and does not have these streets, so this area cannot be built. Use a built-in region instead.'));
    } else {
      line(
        'schedule',
        t('About {time} to build — the server downloads these streets from OpenStreetMap first.', {
          time: formatWait(e.etaSeconds ?? 0),
        }),
        t('Map tiles still to download: {missing} of {total}. Afterwards they are kept, so the next race here is quick.', {
          missing,
          total: e.tiles,
        }),
      );
    }
  }

  /** The name is still one the wizard filled in, rather than one somebody typed. */
  private nameIsSuggested(): boolean {
    return (
      !this.name ||
      this.name === this.request?.defaultTitle ||
      this.fixtures.some((f) => f.title === this.name)
    );
  }

  // -------------------------------------------------------------------------

  private showNotice(message: string): void {
    this.notice.textContent = message;
    this.notice.classList.remove('is-hidden');
    window.clearTimeout(this.noticeTimer);
    this.noticeTimer = window.setTimeout(() => this.hideNotice(), NOTICE_MS);
  }

  private hideNotice(): void {
    window.clearTimeout(this.noticeTimer);
    this.notice.classList.add('is-hidden');
  }

  dispose(): void {
    this.picker.dispose();
  }
}

const TOOLS: Record<StepId, PickerTool> = {
  source: 'view',
  area: 'box',
  pins: 'pins',
  route: 'route',
  check: 'view',
};
